// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — a Hovmöller diagram along the Analyze panel's Line
 * (`docs/AYNI_XR_RESEARCH_TIER_PLAN.md`, slice 3).
 *
 * Hovmöller (1949) drew a field's values along one line, one row per
 * time: distance across, time down, so a wave moving along the line is a
 * slanted stripe and a standing pattern a vertical one. Here the line is
 * the one the Line tool drew (a great circle, sampled one point per grid
 * cell crossed, as its profile is: `datasetStats.sampleTransect`) and the
 * rows fill as playback passes the frames, the way the pins' graph does
 * (`measure/series.ts`).
 *
 * The rules that keep the grid honest:
 *
 * - **Rows are frames, keyed by time.** A row is where a frame sits in the
 *   dataset's window, never the order frames arrived, so a looping
 *   stream fills its holes on the next lap and a frame read again
 *   overwrites its row rather than adding one. With the stream's frame
 *   count known, row i is frame i. Past {@link MAX_HOVMOLLER_ROWS}
 *   frames, several share a row and the newest read wins (said in the
 *   panel and the CSV).
 * - **Two kinds of empty.** A row not read yet will fill; a cell read
 *   with no data in it never will. The store keeps them apart (`read`
 *   per row, and the code per cell), as the series does.
 * - **The same arithmetic as the Line.** {@link texelsAlong} finds each
 *   point's texel exactly as `sampleTransect` does (a release's map
 *   rectangle included), and {@link lineValueTable} decodes a code by the
 *   same rule, so a row of the diagram and the Line's profile of that
 *   frame are the same numbers.
 *
 * Reference: Hovmöller, E. (1949). The trough-and-ridge diagram. Tellus,
 * 1(2), 62–66. https://doi.org/10.3402/tellusa.v1i2.8498
 *
 * Pure: no DOM, no GL.
 */

import { firstDataLuma, MAX_TRANSECT_SAMPLES } from '../datasetStats'
import { latLonToTexelUv } from '../datasetProbe'
import { lumaToValue, releaseOf, type ColorScale } from '../../types/color-scale'
import type { DatasetOverlayOptions } from '../../types'
import { DEFAULT_SERIES_SLOTS } from '../measure/series'
import { sourceCodeStep } from '../decoderRange'

/** One point of the line: where it is, and how far along. */
export interface LinePoint {
  readonly lat: number
  readonly lon: number
  /** Great-circle distance from the line's start, km. */
  readonly distanceKm: number
}

/** The dataset's time window, as the pins' graph has it. */
export interface HovmollerWindow {
  /** The first frame's time and the last frame's: UTC ms, or playback ms for a dataset without dates. */
  readonly startMs: number
  readonly endMs: number
  /** Frames in the window, when the stream says (its `.dsa`); null when unknown. */
  readonly frames: number | null
}

/**
 * Rows at most. 2048 × 512 points is 2 MB of codes; a stream with more
 * frames in its window (hourly data over a year) shares rows.
 */
export const MAX_HOVMOLLER_ROWS = 2048

/** A cell whose point falls outside the dataset (a regional box). */
export const OUTSIDE_CODE = -1

export interface HovmollerRecording {
  readonly points: readonly LinePoint[]
  readonly startMs: number
  readonly endMs: number
  /** Frames in the window (null when unknown). */
  readonly frames: number | null
  readonly rows: number
  /** `rows × points` codes, row-major; {@link OUTSIDE_CODE} outside the dataset. Meaningful only on read rows. */
  readonly codes: Int16Array
  /** 1 once a frame landed in the row. */
  readonly read: Uint8Array
  /** The time of the frame last read into each row. */
  readonly readMs: Float64Array
  /** The row written last (the frame on screen), or -1. */
  lastRow: number
}

/**
 * At most `max` points, evenly picked with both ends kept. The Line
 * already asks for at most 512 (`transectSampleCount`); this holds the
 * cap for any other caller.
 */
export function capPoints(points: readonly LinePoint[], max = MAX_TRANSECT_SAMPLES): LinePoint[] {
  if (points.length <= max) return [...points]
  const out: LinePoint[] = []
  for (let i = 0; i < max; i++) out.push(points[Math.round((i * (points.length - 1)) / (max - 1))])
  return out
}

/** Rows for a window: one per frame when the stream says how many (capped), else as many as a pin's graph has. */
export function rowsForWindow(window: HovmollerWindow): number {
  const frames = window.frames
  if (frames !== null && Number.isFinite(frames) && frames >= 1) return Math.max(2, Math.min(MAX_HOVMOLLER_ROWS, Math.floor(frames)))
  return DEFAULT_SERIES_SLOTS
}

export function createRecording(points: readonly LinePoint[], window: HovmollerWindow): HovmollerRecording | null {
  if (points.length < 2) return null
  if (!Number.isFinite(window.startMs) || !Number.isFinite(window.endMs) || window.endMs <= window.startMs) return null
  const kept = capPoints(points)
  const rows = rowsForWindow(window)
  return {
    points: kept,
    startMs: window.startMs,
    endMs: window.endMs,
    frames: window.frames,
    rows,
    codes: new Int16Array(rows * kept.length),
    read: new Uint8Array(rows),
    readMs: new Float64Array(rows),
    lastRow: -1,
  }
}

/** The row a frame time belongs to, or -1 outside the window (the series' rule, `slotForTime`). */
export function rowForTime(rec: Pick<HovmollerRecording, 'startMs' | 'endMs' | 'rows'>, frameMs: number): number {
  if (!Number.isFinite(frameMs)) return -1
  const span = rec.endMs - rec.startMs
  if (span <= 0) return -1
  const f = (frameMs - rec.startMs) / span
  if (f < 0 || f > 1) return -1
  return Math.min(rec.rows - 1, Math.round(f * (rec.rows - 1)))
}

/**
 * Record a frame's codes along the line. A frame read again overwrites
 * its row (the newest read is as good as the first). Returns the row, or
 * -1 when the time is outside the window or the codes do not fit the line.
 */
export function recordFrame(rec: HovmollerRecording, frameMs: number, codes: ArrayLike<number>): number {
  const n = rec.points.length
  if (codes.length !== n) return -1
  const row = rowForTime(rec, frameMs)
  if (row < 0) return -1
  const at = row * n
  for (let i = 0; i < n; i++) rec.codes[at + i] = codes[i]
  rec.read[row] = 1
  rec.readMs[row] = frameMs
  rec.lastRow = row
  return row
}

/** Rows read so far. */
export function rowsRead(rec: HovmollerRecording): number {
  let n = 0
  for (let i = 0; i < rec.rows; i++) n += rec.read[i]
  return n
}

/** Whether rows and frames are one to one, so a row's time is its frame's stamp. */
export function rowPerFrame(rec: Pick<HovmollerRecording, 'rows' | 'frames'>): boolean {
  return rec.frames !== null && rec.rows === rec.frames
}

/**
 * The time a row stands for: its frame's stamp when rows are frames, else
 * the frame read into it (or the row's place in the window, unread).
 */
export function rowTimeMs(rec: HovmollerRecording, row: number): number {
  const nominal = rec.startMs + ((rec.endMs - rec.startMs) * row) / Math.max(1, rec.rows - 1)
  if (rowPerFrame(rec) || !rec.read[row]) return nominal
  return rec.readMs[row]
}

/** Whether a recording still fits this window (the same axis). */
export function sameWindow(rec: HovmollerRecording, window: HovmollerWindow): boolean {
  return rec.startMs === window.startMs && rec.endMs === window.endMs && rec.frames === window.frames
}

/**
 * The recording on a new window (a live stream gained frames): every read
 * row moves by its frame's time, so nothing read is lost while it stays
 * in the window. A new start is a new axis: rows before it are dropped.
 */
export function regrid(rec: HovmollerRecording, window: HovmollerWindow): HovmollerRecording | null {
  const next = createRecording(rec.points, window)
  if (!next) return null
  const n = rec.points.length
  let lastRow = -1
  for (let row = 0; row < rec.rows; row++) {
    if (!rec.read[row]) continue
    const at = recordFrame(next, rowTimeMs(rec, row), rec.codes.subarray(row * n, row * n + n))
    if (row === rec.lastRow) lastRow = at
  }
  next.lastRow = lastRow
  return next
}

// --- reading the frame -------------------------------------------------

/**
 * Where each point falls in a frame `width × height`: x, y pairs, -1, -1
 * outside the dataset. The same arithmetic `sampleTransect` runs on the
 * Analyze frame (`readValueAt`), including a release's map rectangle,
 * which that frame has already cropped (`cropSnapshot`): so the diagram
 * reads the very texels the Line's profile reads.
 */
export function texelsAlong(
  points: readonly { lat: number; lon: number }[],
  width: number,
  height: number,
  options?: DatasetOverlayOptions,
): Int32Array {
  const out = new Int32Array(points.length * 2).fill(-1)
  if (width < 1 || height < 1) return out
  const crop = options?.releaseEncoding && options.cropRect ? options.cropRect : null
  // The analysis frame's own rectangle (cropSnapshot's rounding).
  const x0 = crop ? Math.max(0, Math.round(crop.u0 * width)) : 0
  const y0 = crop ? Math.max(0, Math.round(crop.v0 * height)) : 0
  const w = crop ? Math.min(width - x0, Math.round(crop.us * width)) : width
  const h = crop ? Math.min(height - y0, Math.round(crop.vs * height)) : height
  if (w <= 0 || h <= 0) return out
  const opts = crop ? { ...options, cropRect: undefined } : options
  for (let i = 0; i < points.length; i++) {
    const uv = latLonToTexelUv(points[i].lat, points[i].lon, opts)
    if (!uv) continue
    out[i * 2] = x0 + Math.min(w - 1, Math.max(0, Math.floor(uv.u * w)))
    out[i * 2 + 1] = y0 + Math.min(h - 1, Math.max(0, Math.floor(uv.v * h)))
  }
  return out
}

/** The codes at those texels of a whole frame (the readback path, and the tests). */
export function codesAtTexels(data: Uint8Array, width: number, texels: Int32Array): Int16Array {
  const out = new Int16Array(texels.length / 2)
  for (let i = 0; i < out.length; i++) {
    const x = texels[i * 2]
    const y = texels[i * 2 + 1]
    out[i] = x < 0 || y < 0 ? OUTSIDE_CODE : data[y * width + x]
  }
  return out
}

/**
 * Each code's value, NaN for no data: the Line's rule (`readValueAt`),
 * codes below the first data code are nothing measured.
 */
export function lineValueTable(scale: ColorScale): Float64Array {
  const first = firstDataLuma(scale)
  const out = new Float64Array(256)
  for (let code = 0; code < 256; code++) out[code] = code < first ? NaN : lumaToValue(code, scale)
  return out
}

/** One cell's value: null outside the dataset or without data. */
export function cellValue(code: number, table: Float64Array): number | null {
  if (code < 0 || code > 255) return null
  const v = table[code]
  return Number.isFinite(v) ? v : null
}

/** One step of the data, the resolution of every value here; null for a log or classified release. */
export function quantisationStep(scale: ColorScale): number | null {
  const release = releaseOf(scale)
  if (release) {
    if (release.kind !== 'luma8-linear') return null
    const step = Math.abs(sourceCodeStep(release))
    return Number.isFinite(step) && step > 0 ? step : null
  }
  const step = Math.abs(scale.vmax - scale.vmin) / 255
  return step > 0 ? step : null
}

// --- the change from a held baseline -------------------------------------

/**
 * The baseline at each point for a row: a held frame's values (the same for
 * every row), or a period's means for the group of the frame read into that
 * row (`climatology.frameOf`); null when the row cannot be compared.
 */
export type RowBaseline = readonly (number | null)[] | ((row: number) => ArrayLike<number | null> | null)

/** The baseline values for one row (NaN or null: none at that point). */
export function baselineRow(base: RowBaseline, row: number): ArrayLike<number | null> | null {
  return typeof base === 'function' ? base(row) : base
}

/**
 * The colour limit for change cells: the 98th percentile of |change| over
 * what is read, rounded up to 1-2-2.5-5 (the change map's rule); null
 * when nothing has a change yet.
 */
export function changeLimit(
  rec: HovmollerRecording,
  table: Float64Array,
  base: RowBaseline,
  nice: (x: number) => number,
): number | null {
  const mags: number[] = []
  const n = rec.points.length
  for (let row = 0; row < rec.rows; row++) {
    if (!rec.read[row]) continue
    const ref = baselineRow(base, row)
    if (!ref) continue
    for (let i = 0; i < n; i++) {
      const v = cellValue(rec.codes[row * n + i], table)
      const b = ref[i]
      if (v !== null && b !== null && Number.isFinite(b)) mags.push(Math.abs(v - (b as number)))
    }
  }
  if (!mags.length) return null
  mags.sort((a, b) => a - b)
  const p98 = mags[Math.min(mags.length - 1, Math.round(0.98 * (mags.length - 1)))]
  return p98 > 0 ? nice(p98) : null
}

// --- the CSV ---------------------------------------------------------------

type Cell = string | number | null | undefined

function cell(value: Cell): string {
  if (value === null || value === undefined) return ''
  const s = String(value)
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
}

function csvRows(list: readonly Cell[][]): string {
  return list.map((r) => r.map(cell).join(',')).join('\r\n')
}

export interface HovmollerCsvContext {
  datasetTitle: string | null
  units?: string
  /** The publisher's units, when the scale was restated for reading. */
  sourceUnits?: string
  vmin: number
  vmax: number
  /** One step of the data; null when it is not uniform. */
  step: number | null
  /** True when the rows run in dates (UTC); false for playback time. */
  dated: boolean
  /**
   * AYNI: a row's date as the frame it holds, at the axis's precision
   * ("1991", "1991-01"; `research/frameTime.ts`); null or absent: the ISO instant.
   */
  formatTime?: ((ms: number) => string | null) | null
  /** The provenance rows every AYNI CSV carries (`research/citation.ts`). */
  provenance?: readonly (readonly Cell[])[] | null
  /**
   * The values as the change from a held baseline: its value at each
   * point and which frame it is. Absent: the dataset's own values.
   */
  change?: { base: readonly (number | null)[]; frameLabel: string | null } | null
  /**
   * AYNI S2: the change from a period's climatology, each row against the
   * mean (and, for `sigma`, σ) of its own frame's group. `at` gives the
   * change of a cell (null where there is none); `values` and `method`
   * say what the numbers are.
   */
  period?: { at: (row: number, i: number, value: number) => number | null; values: string; method: string } | null
}

/** The text the CSV and the panel give as the method. */
export const HOVMOLLER_METHOD =
  'Hovmöller diagram (Hovmöller 1949, Tellus 1(2):62-66, doi:10.3402/tellusa.v1i2.8498): values along the Line (a great circle, or a parallel when the Line follows a latitude: see the latitude of each point), one row per frame read during playback, one column per point; points one per grid cell crossed, as the Line profile; values rounded to the dataset\'s steps' // i18n-exempt: CSV row, English like every CSV row

/**
 * The diagram as a CSV: header rows for each point's distance, latitude
 * and longitude, then one row per frame read, in time order, one column
 * per point. A cell without data is empty, never a number. The header
 * says what the numbers are, how fine they are, and how much of the
 * window was read.
 */
export function buildHovmollerCsvText(rec: HovmollerRecording, table: Float64Array, ctx: HovmollerCsvContext): string {
  const n = rec.points.length
  const total = rec.points[n - 1].distanceKm
  const read = rowsRead(rec)
  const first = rec.points[0]
  const last = rec.points[n - 1]
  const period = ctx.period ?? null
  const change = period ? null : ctx.change ?? null
  const head: Cell[][] = [
    ['dataset', ctx.datasetTitle ?? ''],
    ['analysis', 'hovmoller'],
    ['line_from', first.lat, first.lon],
    ['line_to', last.lat, last.lon],
    ['units', ctx.units ?? ''],
    ...(ctx.sourceUnits ? [['source_units', ctx.sourceUnits] as Cell[]] : []),
    ['value_min', ctx.vmin],
    ['value_max', ctx.vmax],
    ['quantisation_step', ctx.step],
    ['length_km', total],
    ['samples', n],
    ['sample_spacing_km', n > 1 ? total / (n - 1) : 0],
    ['frames_in_window', rec.frames],
    ['rows', rec.rows],
    ['rows_read', read],
    ...(rec.frames !== null && rec.frames > rec.rows ? [['rows_shared', `${rec.frames} frames share ${rec.rows} rows; each row keeps the newest frame read into it`] as Cell[]] : []),
    ['time_axis', ctx.dated ? 'UTC' : 'playback seconds'],
    ['values', period ? period.values : change ? `change from the baseline held at ${change.frameLabel ?? 'a frame without a date'} (value minus baseline)` : 'dataset values'],
    ['method', HOVMOLLER_METHOD],
    ...(period ? [['baseline_method', period.method] as Cell[]] : []),
    ...(ctx.provenance ?? []).map((r) => [...r]),
    [],
    ['distance_km', ...rec.points.map((p) => p.distanceKm)],
    ['lat', ...rec.points.map((p) => p.lat)],
    ['lon', ...rec.points.map((p) => p.lon)],
    ...(change ? [['baseline', ...change.base] as Cell[]] : []),
  ]
  const body: Cell[][] = []
  for (let row = 0; row < rec.rows; row++) {
    if (!rec.read[row]) continue
    const ms = rowTimeMs(rec, row)
    const time = ctx.dated ? ctx.formatTime?.(ms) ?? new Date(ms).toISOString() : Number((ms / 1000).toFixed(3))
    const values: Cell[] = [time]
    for (let i = 0; i < n; i++) {
      const v = cellValue(rec.codes[row * n + i], table)
      const b = change ? change.base[i] : null
      values.push(v === null ? null : period ? period.at(row, i, v) : change ? (b === null ? null : v - b) : v)
    }
    body.push(values)
  }
  return `${csvRows(head)}\r\n${csvRows(body)}\r\n`
}
